import { jsx } from '@emotion/react/jsx-runtime';
import { useTheme } from '@emotion/react';
import { createPolymorphicComponent, useSlot, usePreMeasure } from '@nettbureau/core';
import { useViewportSize } from '@react-aria/utils';
import { useState, useCallback } from 'react';
import { BaseTransition } from './base-transition.js';

/**
 * Component that animates its content in and out by sliding in from outside the screen.
 */ const SlideInOut = createPolymorphicComponent((props, ref)=>{
    const [mergedProps, mergedRef] = useSlot('SlideInOut', props, ref);
    const { children, duration = 'medium', exitDuration = 'fast', from = 'bottom', in: visible, initial, ...elementProps } = mergedProps;
    const theme = useTheme();
    const viewportSize = useViewportSize();
    const [initialPosition, setInitialPosition] = useState(null);
    const [{ measuring, setMeasuring }, measureElement] = usePreMeasure(children, ({ left, width, height })=>{
        return setInitialPosition({
            top: {
                y: -(viewportSize.height / 2 + height / 2)
            },
            bottom: {
                y: viewportSize.height / 2 + height / 2
            },
            left: {
                x: -(left + width)
            },
            right: {
                x: viewportSize.width - left
            }
        }[from]);
    }, [
        visible
    ]);
    const handleExited = useCallback(()=>setMeasuring(true), []);
    const inTransition = {
        type: 'tween',
        duration: typeof duration === 'number' ? duration : theme.motionDurations[duration],
        ease: [
            0.25,
            0.1,
            0.25,
            1
        ]
    };
    const outTransition = {
        type: 'tween',
        duration: typeof exitDuration === 'number' ? exitDuration : theme.motionDurations[exitDuration],
        ease: 'easeIn'
    };
    const enter = {
        x: 0,
        y: 0,
        transition: inTransition
    };
    const exit = {
        ...initialPosition,
        transition: outTransition
    };
    return /*#__PURE__*/ jsx(BaseTransition, {
        ...elementProps,
        ref: mergedRef,
        enter: enter,
        exit: exit,
        initial: initial,
        mounted: visible,
        measuring: measuring,
        onExitComplete: handleExited,
        children: measuring ? measureElement : children
    });
});

export { SlideInOut };
//# sourceMappingURL=slide-in-out.js.map
